import {
	IconActivity,
	IconAlertCircle,
	IconAlertTriangle,
	IconArrowDownRight,
	IconArrowUpRight,
	IconCash,
	IconChecks,
	IconClock,
	IconDeviceMobile,
	IconDeviceSim,
	IconInfoCircle,
	IconMessage,
	IconPercentage,
	IconPlus,
	IconShieldCheck,
	IconTrendingUp,
	IconTrophy,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useAgentSIMs } from "@/hooks/use-agent-sims";
import { useAuth } from "@/hooks/use-auth";
import { useStats } from "@/hooks/use-stats";
import { useSystemAlerts, useSystemHealth } from "@/hooks/use-system-health";
import { useMerchantStats, type MerchantDashboardStats } from "@/hooks/use-merchant-stats";
import { useMerchantSims, type MerchantSimsResponse } from "@/hooks/use-merchant-sims";
import { useMerchantCashFlow } from "@/hooks/use-merchant-cash-flow";

export const Route = createFileRoute("/")({
	component: Dashboard,
});

function Dashboard() {
	return (
		<ProtectedRoute>
			<AppShell>
				<DashboardContent />
			</AppShell>
		</ProtectedRoute>
	);
}

function DashboardContent() {
	const { user } = useAuth();

	// Show appropriate dashboard based on user role
	if (user?.role === "agent") {
		return <AgentDashboard />;
	}

	if (user?.role === "merchant") {
		return <MerchantDashboard />;
	}

	return <AdminDashboard />;
}

function AgentDashboard() {
	const { data: sims, isLoading, error } = useAgentSIMs();

	// Calculate agent statistics with safe fallbacks
	const stats = {
		totalSIMs: sims?.length || 0,
		activeSIMs: sims?.filter((sim) => sim.status === "active").length || 0,
		pendingSIMs: sims?.filter((sim) => sim.status === "pending").length || 0,
		rejectedSIMs: sims?.filter((sim) => sim.status === "rejected").length || 0,
	};

	// Handle loading and error states
	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
					<p className="text-muted-foreground">Loading your SIMs...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<IconAlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
					<p className="text-destructive font-medium">Failed to load SIMs</p>
					<p className="text-sm text-muted-foreground mt-2">
						{error instanceof Error ? error.message : "Unknown error occurred"}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Agent Dashboard</h1>
				<p className="text-muted-foreground">
					Welcome to your agent dashboard. Manage your SIMs and view your
					performance.
				</p>
			</div>

			{/* Agent Stats */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total SIMs</CardTitle>
						<IconDeviceSim className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{stats.totalSIMs}</div>
						<p className="text-xs text-muted-foreground">
							Registered SIM cards
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Active SIMs</CardTitle>
						<IconChecks className="h-4 w-4 text-green-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{stats.activeSIMs}
						</div>
						<p className="text-xs text-muted-foreground">
							Ready for transactions
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Pending</CardTitle>
						<IconClock className="h-4 w-4 text-orange-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-orange-600">
							{stats.pendingSIMs}
						</div>
						<p className="text-xs text-muted-foreground">Awaiting approval</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Success Rate</CardTitle>
						<IconTrophy className="h-4 w-4 text-purple-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-purple-600">
							{stats.totalSIMs > 0
								? ((stats.activeSIMs / stats.totalSIMs) * 100).toFixed(1)
								: 0}
							%
						</div>
						<p className="text-xs text-muted-foreground">
							Approval success rate
						</p>
					</CardContent>
				</Card>
			</div>

			{/* Quick Actions */}
			<div className="grid gap-4 md:grid-cols-2">
				<Card className="border-dashed">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconPlus className="h-5 w-5" />
							Register New SIM
						</CardTitle>
						<CardDescription>
							Add a new SIM card to your account for transactions
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild className="w-full">
							<Link to="/sims/register">
								<IconPlus className="h-4 w-4 mr-2" />
								Register SIM
							</Link>
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconDeviceSim className="h-5 w-5" />
							My SIMs
						</CardTitle>
						<CardDescription>
							View and manage all your registered SIM cards
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild variant="outline" className="w-full">
							<Link to="/sims/my-sims">
								<IconDeviceSim className="h-4 w-4 mr-2" />
								View All SIMs
							</Link>
						</Button>
					</CardContent>
				</Card>
			</div>

			{/* Recent Activity */}
			<Card>
				<CardHeader>
					<CardTitle>Recent SIM Registrations</CardTitle>
					<CardDescription>
						Your latest SIM registration activity
					</CardDescription>
				</CardHeader>
				<CardContent>
					{sims && sims.length > 0 ? (
						<div className="space-y-4">
							{sims.slice(0, 5).map((sim) => (
								<div
									key={sim.id}
									className="flex items-center justify-between p-4 border rounded-lg"
								>
									<div className="space-y-1">
										<div className="flex items-center gap-2">
											<span className="font-mono font-medium">
												{sim.phoneNumber}
											</span>
											<Badge variant="outline" className="capitalize">
												{sim.operator}
											</Badge>
											<Badge
												variant={
													sim.status === "active"
														? "default"
														: sim.status === "pending"
															? "secondary"
															: "destructive"
												}
											>
												{sim.status}
											</Badge>
										</div>
										<p className="text-sm text-muted-foreground">
											Registered {new Date(sim.createdAt).toLocaleDateString()}
										</p>
									</div>
									{sim.status === "active" && (
										<IconChecks className="h-5 w-5 text-green-500" />
									)}
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-8">
							<IconDeviceSim className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
							<p className="text-muted-foreground mb-4">
								No SIMs registered yet
							</p>
							<Button asChild>
								<Link to="/sims/register">
									<IconPlus className="h-4 w-4 mr-2" />
									Register Your First SIM
								</Link>
							</Button>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Help Section */}
			<Card>
				<CardHeader>
					<CardTitle>Getting Started</CardTitle>
					<CardDescription>
						Quick guide to using your agent dashboard
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-4 md:grid-cols-2">
						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									1
								</span>
								Register SIM Cards
							</h4>
							<p className="text-sm text-muted-foreground">
								Add your mobile financial service SIM cards to start processing
								transactions
							</p>
						</div>

						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									2
								</span>
								Wait for Approval
							</h4>
							<p className="text-sm text-muted-foreground">
								Admin will review and approve your SIM registrations
							</p>
						</div>

						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									3
								</span>
								Configure Devices
							</h4>
							<p className="text-sm text-muted-foreground">
								Use the Agent Device App to bind approved SIMs to your devices
							</p>
						</div>

						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									4
								</span>
								Start Earning
							</h4>
							<p className="text-sm text-muted-foreground">
								Process transactions and earn commissions on successful payments
							</p>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function AdminDashboard() {
	const { data: stats, isLoading, error } = useStats();
	const { data: systemHealth, isLoading: isLoadingHealth } = useSystemHealth();
	const { data: systemAlerts, isLoading: isLoadingAlerts } = useSystemAlerts();

	if (isLoading || isLoadingHealth || isLoadingAlerts) {
		return (
			<div className="flex items-center justify-center h-[50vh]">
				<div className="flex flex-col items-center gap-2">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					<p className="text-sm text-muted-foreground">Loading dashboard...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-[50vh]">
				<Card className="max-w-md">
					<CardHeader>
						<CardTitle className="text-destructive">
							Error Loading Dashboard
						</CardTitle>
						<CardDescription>
							{error instanceof Error
								? error.message
								: "An unexpected error occurred"}
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		);
	}

	if (!stats || !systemHealth || !systemAlerts) {
		return null;
	}

	const { today, overall } = stats;

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
				<p className="text-muted-foreground">
					Welcome to your admin dashboard. Here's what's happening today.
				</p>
			</div>

			{/* Today's Stats */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Today's Orders
						</CardTitle>
						<IconTrendingUp className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{today.orders}</div>
						<p className="text-xs text-muted-foreground">
							{today.completed} completed, {today.pending} pending
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Today's Volume
						</CardTitle>
						<IconCash className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							৳{today.volume.toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">
							৳{today.completedVolume.toLocaleString()} completed
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Success Rate</CardTitle>
						<IconChecks className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{today.successRate.toFixed(2)}%
						</div>
						<p className="text-xs text-muted-foreground">
							{today.failed} failed orders
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Net Revenue</CardTitle>
						<IconCash className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							৳{today.commissions.netRevenue.toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">
							From {today.completed} transactions
						</p>
					</CardContent>
				</Card>
			</div>

			{/* Deposits & Payouts */}
			<div className="grid gap-4 md:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconArrowDownRight className="h-5 w-5 text-green-500" />
							Deposits
						</CardTitle>
						<CardDescription>Today's deposit activity</CardDescription>
					</CardHeader>
					<CardContent className="space-y-2">
						<div className="flex justify-between">
							<span className="text-sm text-muted-foreground">Count</span>
							<span className="font-semibold">{today.deposits.count}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-sm text-muted-foreground">Volume</span>
							<span className="font-semibold">
								৳{today.deposits.volume.toLocaleString()}
							</span>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconArrowUpRight className="h-5 w-5 text-red-500" />
							Payouts
						</CardTitle>
						<CardDescription>Today's payout activity</CardDescription>
					</CardHeader>
					<CardContent className="space-y-2">
						<div className="flex justify-between">
							<span className="text-sm text-muted-foreground">Count</span>
							<span className="font-semibold">{today.payouts.count}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-sm text-muted-foreground">Volume</span>
							<span className="font-semibold">
								৳{today.payouts.volume.toLocaleString()}
							</span>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* System Health */}
			{systemHealth && (
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Active Orders
							</CardTitle>
							<IconActivity className="h-4 w-4 text-blue-500" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{systemHealth.activeOrders}
							</div>
							<p className="text-xs text-muted-foreground">
								{systemHealth.ordersInReview} in review
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Devices Status
							</CardTitle>
							<IconDeviceMobile className="h-4 w-4 text-green-500" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-green-600">
								{systemHealth.devicesOnline}
							</div>
							<p className="text-xs text-muted-foreground">
								{systemHealth.devicesOffline} offline
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								Processing Time
							</CardTitle>
							<IconClock className="h-4 w-4 text-purple-500" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold">
								{systemHealth.avgProcessingTime.toFixed(1)}s
							</div>
							<p className="text-xs text-muted-foreground">
								{systemHealth.recentErrors} recent errors
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">
								System Alerts
							</CardTitle>
							<IconShieldCheck className="h-4 w-4 text-orange-500" />
						</CardHeader>
						<CardContent>
							<div className="flex items-center gap-2">
								<div className="text-2xl font-bold text-red-600">
									{systemAlerts.critical.length}
								</div>
								<span className="text-muted-foreground">/</span>
								<div className="text-xl font-semibold text-yellow-600">
									{systemAlerts.warnings.length}
								</div>
							</div>
							<p className="text-xs text-muted-foreground">
								Critical / Warnings
							</p>
						</CardContent>
					</Card>
				</div>
			)}

			{/* System Alerts */}
			{systemAlerts &&
				(systemAlerts.critical.length > 0 ||
					systemAlerts.warnings.length > 0) && (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<IconAlertTriangle className="h-5 w-5 text-orange-500" />
								System Alerts
							</CardTitle>
							<CardDescription>
								Active system notifications and warnings
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							{systemAlerts.critical.length > 0 && (
								<div className="space-y-2">
									<div className="flex items-center gap-2 text-sm font-medium text-red-600">
										<IconAlertCircle className="h-4 w-4" />
										Critical Alerts
									</div>
									{systemAlerts.critical.map((alert, idx) => (
										<div
											key={idx}
											className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 p-3"
										>
											<div className="flex items-start justify-between gap-2">
												<div className="flex-1">
													<div className="flex items-center gap-2">
														<Badge variant="destructive">{alert.type}</Badge>
														{alert.count > 1 && (
															<span className="text-xs text-muted-foreground">
																({alert.count}x)
															</span>
														)}
													</div>
													<p className="text-sm mt-1">{alert.message}</p>
												</div>
												<span className="text-xs text-muted-foreground whitespace-nowrap">
													{new Date(alert.timestamp).toLocaleTimeString()}
												</span>
											</div>
										</div>
									))}
								</div>
							)}

							{systemAlerts.warnings.length > 0 && (
								<div className="space-y-2">
									<div className="flex items-center gap-2 text-sm font-medium text-yellow-600">
										<IconAlertCircle className="h-4 w-4" />
										Warnings
									</div>
									{systemAlerts.warnings.map((alert, idx) => (
										<div
											key={idx}
											className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 p-3"
										>
											<div className="flex items-start justify-between gap-2">
												<div className="flex-1">
													<div className="flex items-center gap-2">
														<Badge
															variant="outline"
															className="border-yellow-600 text-yellow-600"
														>
															{alert.type}
														</Badge>
														{alert.count > 1 && (
															<span className="text-xs text-muted-foreground">
																({alert.count}x)
															</span>
														)}
													</div>
													<p className="text-sm mt-1">{alert.message}</p>
												</div>
												<span className="text-xs text-muted-foreground whitespace-nowrap">
													{new Date(alert.timestamp).toLocaleTimeString()}
												</span>
											</div>
										</div>
									))}
								</div>
							)}

							{systemAlerts.info.length > 0 && (
								<div className="space-y-2">
									<div className="flex items-center gap-2 text-sm font-medium text-blue-600">
										<IconInfoCircle className="h-4 w-4" />
										Information
									</div>
									{systemAlerts.info.map((alert, idx) => (
										<div
											key={idx}
											className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 p-3"
										>
											<div className="flex items-start justify-between gap-2">
												<div className="flex-1">
													<div className="flex items-center gap-2">
														<Badge
															variant="outline"
															className="border-blue-600 text-blue-600"
														>
															{alert.type}
														</Badge>
														{alert.count > 1 && (
															<span className="text-xs text-muted-foreground">
																({alert.count}x)
															</span>
														)}
													</div>
													<p className="text-sm mt-1">{alert.message}</p>
												</div>
												<span className="text-xs text-muted-foreground whitespace-nowrap">
													{new Date(alert.timestamp).toLocaleTimeString()}
												</span>
											</div>
										</div>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				)}

			{/* Overall Stats */}
			<Card>
				<CardHeader>
					<CardTitle>Overall Statistics</CardTitle>
					<CardDescription>Platform-wide metrics</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
						<div>
							<div className="text-sm text-muted-foreground">Merchants</div>
							<div className="text-2xl font-bold">{overall.merchants}</div>
						</div>
						<div>
							<div className="text-sm text-muted-foreground">Agents</div>
							<div className="text-2xl font-bold">{overall.agents}</div>
						</div>
						<div>
							<div className="text-sm text-muted-foreground">Total Orders</div>
							<div className="text-2xl font-bold">
								{overall.totalOrders.toLocaleString()}
							</div>
						</div>
						<div>
							<div className="text-sm text-muted-foreground">Total Volume</div>
							<div className="text-2xl font-bold">
								৳{overall.totalVolume.toLocaleString()}
							</div>
						</div>
						<div>
							<div className="text-sm text-muted-foreground">
								Completed Volume
							</div>
							<div className="text-2xl font-bold">
								৳{overall.completedVolume.toLocaleString()}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function MerchantDashboard() {
	const {
		data: stats,
		isLoading: isLoadingStats,
		error: statsError,
	} = useMerchantStats();
	const { data: simsData, isLoading: isLoadingSims } = useMerchantSims();
	const { data: cashFlow, isLoading: isLoadingCashFlow } =
		useMerchantCashFlow("today");

	// Type assertions for proper type checking
	const merchantStats = stats as MerchantDashboardStats | undefined;
	const merchantSims = simsData as MerchantSimsResponse | undefined;

	if (isLoadingStats || isLoadingSims || isLoadingCashFlow) {
		return (
			<div className="flex items-center justify-center h-[50vh]">
				<div className="flex flex-col items-center gap-2">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
					<p className="text-sm text-muted-foreground">
						Loading your dashboard...
					</p>
				</div>
			</div>
		);
	}

	if (statsError) {
		return (
			<div className="flex items-center justify-center h-[50vh]">
				<Card className="max-w-md">
					<CardHeader>
						<CardTitle className="text-destructive">
							Error Loading Dashboard
						</CardTitle>
						<CardDescription>
							{statsError instanceof Error
								? statsError.message
								: "An unexpected error occurred"}
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		);
	}

	if (!merchantStats || !merchantSims || !cashFlow) {
		return null;
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">
					Merchant Dashboard
				</h1>
				<p className="text-muted-foreground">
					Welcome back, {merchantStats.merchant.businessName}
				</p>
			</div>

			{/* Today's Stats */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Today's Orders
						</CardTitle>
						<IconTrendingUp className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{merchantStats.today.total}</div>
						<p className="text-xs text-muted-foreground">
							{merchantStats.today.completed} completed, {merchantStats.today.pending} pending
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Today's Volume
						</CardTitle>
						<IconCash className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							৳{Number(merchantStats.today.volume).toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">
							৳{Number(merchantStats.today.completed).toLocaleString()} completed
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Success Rate</CardTitle>
						<IconChecks className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{merchantStats.overall.successRate}
						</div>
						<p className="text-xs text-muted-foreground">
							{merchantStats.today.failed} failed orders
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Balance</CardTitle>
						<IconCash className="h-4 w-4 text-purple-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-purple-600">
							৳{Number(merchantStats.merchant.balance).toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">Available balance</p>
					</CardContent>
				</Card>
			</div>

			{/* Monthly Stats */}
			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							This Month - Volume
						</CardTitle>
						<IconTrendingUp className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							৳{Number(merchantStats.thisMonth.volume).toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">
							{merchantStats.thisMonth.total} orders
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Pending Webhooks
						</CardTitle>
						<IconMessage className="h-4 w-4 text-orange-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-orange-600">
							{merchantStats.pendingWebhooks}
						</div>
						<p className="text-xs text-muted-foreground">
							Webhooks pending delivery
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Commission Rate
						</CardTitle>
						<IconPercentage className="h-4 w-4 text-purple-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-purple-600">
							{merchantStats.merchant.commissionRate}%
						</div>
						<p className="text-xs text-muted-foreground">Per transaction</p>
					</CardContent>
				</Card>
			</div>

			{/* Assigned SIMs */}
			<Card>
				<CardHeader>
					<CardTitle>Your Assigned SIMs</CardTitle>
					<CardDescription>
						SIM cards assigned to process your transactions (
						{merchantSims.total ?? 0} total)
					</CardDescription>
				</CardHeader>
				<CardContent>
					{merchantSims.sims && merchantSims.sims.length > 0 ? (
						<div className="space-y-4">
							{/* SIM Breakdown by Operator */}
							<div className="grid gap-4 md:grid-cols-5">
								{Object.entries(merchantSims.byOperator).map(
									([operator, count]) =>
										(count as number) > 0 && (
											<div
												key={operator}
												className="text-center p-3 rounded-lg border"
											>
												<div className="text-sm text-muted-foreground capitalize">
													{operator}
												</div>
												<div className="text-2xl font-bold">
													{count as number}
												</div>
											</div>
										),
								)}
							</div>

							{/* SIM List */}
							<div className="space-y-2">
								<h4 className="text-sm font-medium">Active SIMs</h4>
								<div className="grid gap-2 md:grid-cols-2">
									{merchantSims.sims
										.filter((s: { status: string }) => s.status === "active")
										.map((sim: { id: string; phoneNumber: string; operator: string; currentBalance?: string }) => (
											<div
												key={sim.id}
												className="flex items-center justify-between p-3 border rounded-lg"
											>
												<div className="space-y-1">
													<div className="flex items-center gap-2">
														<span className="font-mono font-medium">
															{sim.phoneNumber}
														</span>
														<Badge variant="outline" className="capitalize">
															{sim.operator}
														</Badge>
													</div>
													<p className="text-xs text-muted-foreground">
														Balance: ৳
														{Number(sim.currentBalance || 0).toLocaleString()}
													</p>
												</div>
												<IconChecks className="h-5 w-5 text-green-500" />
											</div>
										))}
								</div>
							</div>
						</div>
					) : (
						<div className="text-center py-8">
							<IconDeviceSim className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
							<p className="text-muted-foreground mb-2">No SIMs assigned yet</p>
							<p className="text-sm text-muted-foreground">
								Contact admin to assign SIMs to your merchant account
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Quick Links */}
			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconActivity className="h-5 w-5" />
							View Orders
						</CardTitle>
						<CardDescription>
							Monitor all your deposit and payout orders
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild className="w-full">
							<Link to="/orders">
								<IconActivity className="h-4 w-4 mr-2" />
								View Orders
							</Link>
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconCash className="h-5 w-5" />
							Cash Flow Analysis
						</CardTitle>
						<CardDescription>
							Detailed cash flow reports and analytics
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild variant="outline" className="w-full">
							<Link to="/analytics">
								<IconCash className="h-4 w-4 mr-2" />
								View Analytics
							</Link>
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconDeviceSim className="h-5 w-5" />
							SIM Management
						</CardTitle>
						<CardDescription>
							View and manage your assigned SIM cards
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild variant="outline" className="w-full">
							<Link to="/sims">
								<IconDeviceSim className="h-4 w-4 mr-2" />
								View SIMs
							</Link>
						</Button>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
